import { useEffect, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import api from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { Save, AlertTriangle } from "lucide-react";
import { toast } from "sonner";

function fmtDate(s) {
  if (!s) return "—";
  try { return new Date(s).toLocaleString("es-MX", { dateStyle: "medium", timeStyle: "short" }); }
  catch { return s; }
}

export default function AdminPlans() {
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({
    queryKey: ["admin-plans"],
    queryFn: async () => (await api.get("/admin/plans")).data,
  });

  // local edits keyed by plan_id
  const [edits, setEdits] = useState({});
  useEffect(() => {
    if (data) {
      const init = {};
      for (const p of data) init[p.plan_id] = { amount: String(p.amount), min_seats: String(p.min_seats || 1) };
      setEdits(init);
    }
  }, [data]);

  const save = useMutation({
    mutationFn: async ({ plan_id, amount, min_seats, per_seat }) => {
      const body = { amount: Number(amount) };
      if (per_seat) body.min_seats = Number(min_seats);
      return (await api.put(`/admin/plans/${plan_id}`, body)).data;
    },
    onSuccess: () => {
      toast.success("Precio actualizado. PayPal cobrará el nuevo monto en próximas compras.");
      qc.invalidateQueries({ queryKey: ["admin-plans"] });
      qc.invalidateQueries({ queryKey: ["public-plans"] });
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "Error al guardar"),
  });

  if (isLoading || !data) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-8 w-1/3" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  // Group by plan + cycle for cleaner UI
  const groups = data.reduce((acc, p) => {
    const key = `${p.plan}_${p.cycle}`;
    (acc[key] ||= []).push(p);
    return acc;
  }, {});
  const groupOrder = [
    { key: "pro_monthly", title: "Pro · Mensual" },
    { key: "pro_annual", title: "Pro · Anual" },
    { key: "corporate_monthly", title: "Corporativo · Mensual (por usuario)" },
    { key: "corporate_annual", title: "Corporativo · Anual (por usuario)" },
  ];

  return (
    <div className="space-y-6" data-testid="admin-plans-page">
      <div>
        <h1 className="text-2xl md:text-3xl font-semibold">Planes y precios</h1>
        <p className="text-sm text-muted-foreground mt-1">
          Edita el precio que PayPal cobrará a los nuevos suscriptores. Los cambios se reflejan inmediatamente en la landing y en `/pricing`.
        </p>
      </div>

      <div className="rounded-2xl border border-amber-500/30 bg-amber-500/5 p-4 flex items-start gap-3" data-testid="admin-plans-warning">
        <AlertTriangle className="h-5 w-5 text-amber-500 flex-none mt-0.5" />
        <div className="text-sm">
          <p className="font-medium">Estos precios afectan compras nuevas</p>
          <p className="text-xs text-muted-foreground mt-0.5">
            Las suscripciones ya activas mantienen el precio con el que se contrataron. Solo los checkouts a partir de ahora usarán los nuevos montos.
          </p>
        </div>
      </div>

      {groupOrder.map(({ key, title }) => {
        const items = groups[key] || [];
        if (items.length === 0) return null;
        return (
          <Card key={key} className="border-white/40 dark:border-white/10 bg-white/40 dark:bg-slate-900/40 backdrop-blur-xl">
            <CardHeader className="pb-3">
              <CardTitle className="text-base">{title}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              {items.map(plan => {
                const e = edits[plan.plan_id] || { amount: String(plan.amount), min_seats: String(plan.min_seats || 1) };
                const changed =
                  Number(e.amount) !== Number(plan.amount) ||
                  (plan.per_seat && Number(e.min_seats) !== Number(plan.min_seats || 1));
                return (
                  <div
                    key={plan.plan_id}
                    data-testid={`admin-plan-${plan.plan_id}`}
                    className="rounded-xl border border-white/40 dark:border-white/10 bg-white/30 dark:bg-slate-900/30 p-4 flex flex-col md:flex-row md:items-end gap-3"
                  >
                    <div className="flex-1 min-w-0">
                      <div className="text-[10px] uppercase tracking-wider text-muted-foreground">{plan.plan_id}</div>
                      <div className="text-sm font-medium mt-0.5">
                        {plan.currency.toUpperCase()} · ciclo de {plan.days} días{plan.per_seat ? " · cobrado por usuario" : ""}
                      </div>
                      <div className="text-[11px] text-muted-foreground mt-1">
                        Última actualización: {fmtDate(plan.updated_at)} · por {plan.updated_by || "system"}
                      </div>
                    </div>
                    <div className="flex items-end gap-2 flex-none">
                      <div className="space-y-1">
                        <label className="text-[10px] uppercase tracking-wider text-muted-foreground">Precio ({plan.currency.toUpperCase()})</label>
                        <Input
                          data-testid={`admin-plan-amount-${plan.plan_id}`}
                          type="number"
                          step="0.01"
                          min="0.01"
                          value={e.amount}
                          onChange={(ev) => setEdits(prev => ({ ...prev, [plan.plan_id]: { ...e, amount: ev.target.value } }))}
                          className="w-32"
                        />
                      </div>
                      {plan.per_seat && (
                        <div className="space-y-1">
                          <label className="text-[10px] uppercase tracking-wider text-muted-foreground">Min. usuarios</label>
                          <Input
                            data-testid={`admin-plan-minseats-${plan.plan_id}`}
                            type="number"
                            min="1"
                            value={e.min_seats}
                            onChange={(ev) => setEdits(prev => ({ ...prev, [plan.plan_id]: { ...e, min_seats: ev.target.value } }))}
                            className="w-24"
                          />
                        </div>
                      )}
                      <Button
                        data-testid={`admin-plan-save-${plan.plan_id}`}
                        disabled={!changed || save.isPending}
                        onClick={() => save.mutate({
                          plan_id: plan.plan_id,
                          amount: e.amount,
                          min_seats: e.min_seats,
                          per_seat: plan.per_seat,
                        })}
                        className="gap-1.5"
                      >
                        <Save className="h-3.5 w-3.5" />
                        Guardar
                      </Button>
                    </div>
                  </div>
                );
              })}
            </CardContent>
          </Card>
        );
      })}
    </div>
  );
}
